'use client';
import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import Catalogs from './../../../components/catalogs';
import ProductCard from './../../../components/product-card';
import { useQueryCatalogInfo, useQueryCategorySearchResult } from '@/hooks/search-result.hook';
import { useSearchParams, useParams } from 'next/navigation';
import { Breadcrumb, GlobalLoading, Pagination } from '@/shared/components';
import NotFoundSearchResult from './../../../components/not-found';
import { NAMESPACE, useLang } from '@/hooks/use-lang';
import languageContext from '@/context/language';
import './styles.css';
import { ProductImage } from '@/shared/pageComponents';
import { COUNTRY } from '@/app/utils/constant';
import { usePageTracking, useEventTracking } from '@/shared/tracking/use-tracking';
import { useSearchType, usePlacement } from '@/shared/tracking/use-tracking-helpers';
import { searchFilterTracking } from '@/shared/tracking/utils';
import { scrollToTop, uniqueTitle } from '@/app/utils';
import { CatalogProducts } from '@/types/search-result';

export default function CategorySearch() {
  const { t } = useLang(NAMESPACE.common);
  const searchParams = useSearchParams();
  const { lang } = useContext(languageContext);
  const [pageSize, setPageSize] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState(searchParams.get('filter') || '');
  const [showReadMore, setShowReadMore] = useState<boolean>(false);
  const [showMoreText, setShowMoreText] = useState<boolean>(false);
  const [moreText, setMoreText] = useState<string>(t('read.more'));
  const [descData, setDescData] = useState<string>('');
  const [refinement, setRefinement] = useState([]);
  const [descClassName, setClassName] = useState<string>('text-base w-9/12 text-gray-1000');
  const containerRef = useRef(null);
  const searchType = useSearchType();
  const { trackEventEffect } = useEventTracking();
  const placement = usePlacement();
  const params = useParams<{
    slug: string;
  }>();
  const { data, isLoading, isSuccess } = useQueryCategorySearchResult({
    countryCode: COUNTRY,
    language: lang,
    page: currentPage,
    pageSize,
    categoryId: params.slug,
    filter,
  });

  const [showCatalogResultDTOs, setShowCatalogResultDTOs] = useState<CatalogProducts[]>([]);
  const { data: catalogInfo, mutate } = useQueryCatalogInfo();

  useEffect(() => {
    scrollToTop();
    if (data) {
      setShowCatalogResultDTOs(data?.catalogResultDTOs ?? []);
      mutate(data?.catalogResultDTOs?.map((item) => item.childCatalogNumber));
    }
  }, [data]);

  useEffect(() => {
    if (catalogInfo) {
      setShowCatalogResultDTOs((prev) => {
        return prev.map((product) => {
          const catalogInfoByCatalogNumber = catalogInfo?.find(
            (item) => item.catalogNumber === product.childCatalogNumber
          );
          return {
            ...product,
            catalogBrand: catalogInfoByCatalogNumber?.brandName || product?.brand,
            catalogTitle: catalogInfoByCatalogNumber?.catalogTitle || product?.catalogName,
          };
        });
      });
    }
  }, [catalogInfo]);

  usePageTracking(
    {
      items: [
        {
          key: 'search',
          payload: {
            searchType: searchType,
            keyword: params.slug,
            totalResults: data?.count,
          },
        },
        {
          key: 'productImpressions',
          payload:
            Array.isArray(data?.catalogResultDTOs) && data?.catalogResultDTOs.length > 0
              ? data?.catalogResultDTOs?.map((product) => {
                  return {
                    name: uniqueTitle(product.catalogName),
                    catalogId: product.childCatalogNumber,
                    placement: placement,
                  };
                })
              : [],
        },
      ],
    },
    !!data
  );

  useEffect(() => {
    if (!descData) return;
    const containerHeight = containerRef?.current?.clientHeight;
    if (containerHeight > 160) {
      setShowReadMore(true);
      setClassName('hidden');
    } else if (containerHeight !== undefined) {
      setClassName('hidden');
    }
  }, [descData]);

  useEffect(() => {
    if (!data?.category?.description) return;
    setDescData(data?.category?.description);
  }, [data?.category?.description]);

  useEffect(() => {
    if (isLoading) {
      GlobalLoading.show();
    } else {
      GlobalLoading.hide();
    }
  }, [isLoading]);

  useEffect(() => {
    if (data) {
      if (showMoreText) {
        setClassName('text-base w-9/12');
        setMoreText(t('read.less'));
      } else {
        setMoreText(t('read.more'));
        setClassName('hidden');
      }
    }
  }, [showMoreText]);

  trackEventEffect(
    {
      key: 'search',
      action: 'search:filtered',
      payload: {
        searchType: searchType,
        keyword: params.slug,
        filters: searchFilterTracking(filter, data?.refinements.length > 0 ? data?.refinements : refinement),
      },
    },
    filter && data?.refinements ? true : false
  );

  const onFiltersChange = (filter) => {
    setRefinement(data?.refinements);
    setFilter(filter);
    setCurrentPage(1);
  };
  const onPageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const breadCrumbs = useMemo(() => {
    const breadCrumbs = data?.breadcrumbsDTO?.breadcrumbs || [];
    return breadCrumbs.map((item) => ({
      name: item.name,
      url: `/apac/search/category/${item.id}`,
    }));
  }, [data?.breadcrumbsDTO?.breadcrumbs]);

  if (isSuccess) {
    if (!data?.category) {
      return <NotFoundSearchResult query={params.slug} />;
    }
  }
  return (
    <>
      {data?.category && (
        <div className="bg-[#f7f7f7] p-2">
          <Breadcrumb className="mt-6" items={[{ name: t('home'), url: '/' }, ...breadCrumbs]} />
          <div className="flex sm:flex-col mt-4 sm:items-center">
            <ProductImage
              className="mr-5 sm:mb-5"
              src={data?.category?.categoryImage}
              width={220}
              height={176}
              bordered={false}
            />
            <div>
              <h1>{data?.category?.categoryName}</h1>
              {!showMoreText && (
                <p
                  className="text-base w-9/12 description-line"
                  dangerouslySetInnerHTML={{ __html: descData ?? '' }}
                ></p>
              )}
              <p className={descClassName} ref={containerRef} dangerouslySetInnerHTML={{ __html: descData ?? '' }}></p>
              {showReadMore && (
                <p className="cursor-pointer text-[#1e8ae7]" onClick={() => setShowMoreText(!showMoreText)}>
                  {moreText}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="flex sm:flex-col md:flex-col gap-x-10">
        <Catalogs catalogs={data?.refinements} onFiltersChange={onFiltersChange} />
        <div className="flex flex-col flex-1">
          {showCatalogResultDTOs?.map((product, i) => (
            <ProductCard key={product.rootCatalogNumber} product={product} index={i} />
          ))}
        </div>
      </div>
      {isSuccess && (
        <Pagination
          total={data?.count}
          pageSizeOptions={[15, 30, 60]}
          showSizeChanger
          pageSize={pageSize}
          currentPage={currentPage}
          onPageChange={handlePageClick}
          onPageSizeChange={onPageSizeChange}
        />
      )}
    </>
  );
}
